import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useDocumentTitle from '../hooks/useDocumentTitle';
import SEOHead from '../components/SEOHead';
import axios from '../utils/axiosConfig';
import { ensureCsrfToken } from '../utils/csrf';
import { useNotification } from '../contexts/NotificationContext';

type Difficulty = 'easy' | 'medium' | 'hard';
type AnswerOption = 'A' | 'B' | 'C' | 'D';

interface AdminQuestion {
  id: string;
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_answer: 'A' | 'B' | 'C' | 'D';
  category: string;
  main_topic: string | null;
  difficulty: Difficulty;
  date_active: string | null;
  last_asked_date: string | null;
  created_at: string;
  updated_at: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface SubtopicRow {
  id: string;
  main_topic: string;
  subtopic: string;
  total_questions: number;
  easy_count: number;
  medium_count: number;
  hard_count: number;
  target_total: number;
  target_easy: number;
  target_medium: number;
  target_hard: number;
  needed_total: number;
  needed_easy: number;
  needed_medium: number;
  needed_hard: number;
  is_below_total_target: boolean;
  has_difficulty_imbalance: boolean;
  isNiche?: boolean;
  /** @deprecated Casual removed in overhaul; kept for API compatibility */
  casualQuizCount?: number;
}

interface TopicRow {
  mainTopic: string;
  subtopicCount: number;
  belowTargetCount: number;
  neededTotal: number;
  subtopics: SubtopicRow[];
}

interface ContentTopicsResponse {
  success: boolean;
  data: {
    topics: TopicRow[];
    pendingCandidates: number;
    processingJobs: number;
    mainTopics: string[];
  };
  message?: string;
}

interface TopicSuggestionItem {
  topicName: string;
  subtopicName: string;
  reasoning: string;
  estimatedDifficulty: Difficulty;
}

type SubtopicQuestionsState = {
  loading: boolean;
  error: string | null;
  page: number;
  pagination: Pagination | null;
  questions: AdminQuestion[];
};

interface AnswerDistribution {
  total: number;
  counts: Record<AnswerOption, number>;
}

const AdminContentManager: React.FC = () => {
  useDocumentTitle('Cognidaily | Content Manager');
  const { showInfo, showSuccess, showError } = useNotification();

  // Confirmation dialog helper
  const confirmAction = (message: string, onConfirm: () => void, onCancel?: () => void) => {
    setConfirmDialogConfig({ message, onConfirm, onCancel });
    setShowConfirmDialog(true);
  };

  const handleConfirm = async () => {
    if (confirmDialogConfig) {
      try {
        await confirmDialogConfig.onConfirm();
      } catch (error) {
        console.error('[AdminContentManager] Confirmation action failed:', error);
      }
      setShowConfirmDialog(false);
      setConfirmDialogConfig(null);
    }
  };

  const handleCancel = () => {
    if (confirmDialogConfig?.onCancel) {
      confirmDialogConfig.onCancel();
    }
    setShowConfirmDialog(false);
    setConfirmDialogConfig(null);
  };

  const [topics, setTopics] = useState<TopicRow[]>([]);
  const [mainTopics, setMainTopics] = useState<string[]>([]);
  const [pendingCandidates, setPendingCandidates] = useState(0);
  const [processingJobs, setProcessingJobs] = useState(0);

  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(() => new Set());
  const [expandedSubtopics, setExpandedSubtopics] = useState<Set<string>>(() => new Set());
  const [generatingSubtopic, setGeneratingSubtopic] = useState<string | null>(null);
  const [generatingBulk, setGeneratingBulk] = useState(false);
  const [savingNicheKey, setSavingNicheKey] = useState<string | null>(null);

  const [filterMainTopic, setFilterMainTopic] = useState<string>('');
  const [onlyBelowTarget, setOnlyBelowTarget] = useState(false);
  const [search, setSearch] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per-subtopic questions view
  const [subtopicQuestions, setSubtopicQuestions] = useState<Record<string, SubtopicQuestionsState>>({});
  const QUESTIONS_PAGE_SIZE = 50;

  // Question CRUD modal
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<AdminQuestion | null>(null);
  const [activeQuestionKey, setActiveQuestionKey] = useState<string | null>(null);
  const [questionSaving, setQuestionSaving] = useState(false);
  const [questionModalError, setQuestionModalError] = useState<string | null>(null);
  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: '',
    correct_answer: 'A' as 'A' | 'B' | 'C' | 'D',
    category: '',
    difficulty: 'hard' as Difficulty
  });

  // Bulk move subtopic/category modal
  const [showMoveSubtopicModal, setShowMoveSubtopicModal] = useState(false);
  const [moveSubtopicKey, setMoveSubtopicKey] = useState<string | null>(null);
  const [moveFromCategory, setMoveFromCategory] = useState('');
  const [moveFromMainTopic, setMoveFromMainTopic] = useState('');

  const [moveMainTopicMode, setMoveMainTopicMode] = useState<'existing' | 'new'>('existing');
  const [moveSelectedMainTopic, setMoveSelectedMainTopic] = useState('');
  const [moveNewMainTopic, setMoveNewMainTopic] = useState('');

  const [moveSubtopicMode, setMoveSubtopicMode] = useState<'existing' | 'new'>('existing');
  const [moveSelectedSubtopic, setMoveSelectedSubtopic] = useState('');
  const [moveNewSubtopic, setMoveNewSubtopic] = useState('');

  const [movingSubtopic, setMovingSubtopic] = useState(false);
  const [moveSubtopicError, setMoveSubtopicError] = useState<string | null>(null);

  // Create topic modal
  const [showCreateTopicModal, setShowCreateTopicModal] = useState(false);
  const [createTopicMain, setCreateTopicMain] = useState('');
  const [createTopicMainMode, setCreateTopicMainMode] = useState<'existing' | 'new'>('existing');
  const [createTopicSubtopic, setCreateTopicSubtopic] = useState('');
  const [creatingTopic, setCreatingTopic] = useState(false);
  const [createTopicMessage, setCreateTopicMessage] = useState<string | null>(null);
  const [createTopicError, setCreateTopicError] = useState<string | null>(null);

  // Suggestions modal
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<TopicSuggestionItem[]>([]);
  const [acceptingSuggestion, setAcceptingSuggestion] = useState<string | null>(null);

  // Duplicate cleanup modal
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [duplicatesError, setDuplicatesError] = useState<string | null>(null);
  const [duplicateGroups, setDuplicateGroups] = useState<Array<{
    keepId: string;
    duplicates: Array<{
      id: string;
      question_text: string;
      category: string;
      difficulty: string;
      created_at: string;
    }>;
  }>>([]);
  const [totalDuplicates, setTotalDuplicates] = useState(0);
  const [cleaningDuplicates, setCleaningDuplicates] = useState(false);

  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogConfig, setConfirmDialogConfig] = useState<{
    message: string;
    onConfirm: () => void;
    onCancel?: () => void;
  } | null>(null);

  // Competitive quiz history for questions
  const [questionCompetitiveHistory, setQuestionCompetitiveHistory] = useState<Record<string, { dates: string[]; totalCount: number; lastAsked: string | null }>>({});

  // Answer distribution state
  const [answerDistribution, setAnswerDistribution] = useState<AnswerDistribution | null>(null);
  const [distributionLoading, setDistributionLoading] = useState(false);
  const [distributionError, setDistributionError] = useState<string | null>(null);
  const [redistributeFrom, setRedistributeFrom] = useState<AnswerOption>('A');
  const [redistributeTarget, setRedistributeTarget] = useState<'random' | AnswerOption>('random');
  const [redistributeLimit, setRedistributeLimit] = useState(100);
  const [redistributeLoading, setRedistributeLoading] = useState(false);

  // Generation state
  const [generatingTopic, setGeneratingTopic] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_generationConfig, _setGenerationConfig] = useState({ hard: 30, medium: 15, easy: 5 });

  // Editing modals
  const [showEditTopicModal, setShowEditTopicModal] = useState(false);
  const [showEditSubtopicModal, setShowEditSubtopicModal] = useState(false);
  const [editingTopicName, setEditingTopicName] = useState('');
  const [editingSubtopicName, setEditingSubtopicName] = useState('');
  const [editingSubtopicMainTopic, setEditingSubtopicMainTopic] = useState('');
  const [editingSubtopicMainTopicMode, setEditingSubtopicMainTopicMode] = useState<'existing' | 'new'>('existing');
  const [editingSubtopicNameMode, setEditingSubtopicNameMode] = useState<'existing' | 'new'>('existing');
  const [editingTopicKey, setEditingTopicKey] = useState<string | null>(null);
  const [editingSubtopicKey, setEditingSubtopicKey] = useState<string | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  
  // Question form - category dropdown state
  const [questionCategoryMode, setQuestionCategoryMode] = useState<'existing' | 'new'>('existing');
  const [questionMainTopic, setQuestionMainTopic] = useState('');
  const [questionMainTopicMode, setQuestionMainTopicMode] = useState<'existing' | 'new'>('existing');

  // AI suggestions state
  const [suggestionMainTopicFilter, setSuggestionMainTopicFilter] = useState('');
  const [suggestionReviewExisting, setSuggestionReviewExisting] = useState(false);
  const [suggestionCustomInstructions, setSuggestionCustomInstructions] = useState('');

  // Add topic modal - "Add Another" functionality
  const [showAddAnother, setShowAddAnother] = useState(false);

  const queryParams = useMemo(() => {
    const p: Record<string, string> = {};
    if (filterMainTopic) p.mainTopic = filterMainTopic;
    if (onlyBelowTarget) p.onlyBelowTarget = 'true';
    if (search.trim()) p.search = search.trim();
    return p;
  }, [filterMainTopic, onlyBelowTarget, search]);

  const loadTopics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get<ContentTopicsResponse>('/api/admin/content/topics', { params: queryParams });
      if (!res.data.success) throw new Error(res.data.message || 'Failed to load content');
      setTopics(res.data.data.topics || []);
      setMainTopics(res.data.data.mainTopics || []);
      setPendingCandidates(res.data.data.pendingCandidates || 0);
      setProcessingJobs(res.data.data.processingJobs || 0);
    } catch (err: any) {
      console.error('[AdminContentManager] Failed to load topics', err);
      setError(err?.response?.data?.message || 'Failed to load content manager.');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  const loadAnswerDistribution = useCallback(async () => {
    try {
      setDistributionLoading(true);
      setDistributionError(null);
      const res = await axios.get<{
        success: boolean;
        data: AnswerDistribution;
        message?: string;
      }>('/api/admin/questions/answer-distribution');
      if (!res.data.success) throw new Error(res.data.message || 'Failed to load answer distribution');
      setAnswerDistribution(res.data.data);
    } catch (err: any) {
      console.error('[AdminContentManager] Failed to load answer distribution', err);
      setDistributionError(err?.response?.data?.message || err?.message || 'Failed to load answer distribution.');
    } finally {
      setDistributionLoading(false);
    }
  }, []);

  // Load competitive quiz history for a question
  const loadQuestionCompetitiveHistory = useCallback(async (questionId: string) => {
    try {
      const res = await axios.get<{
        success: boolean;
        data: { dates: string[]; totalCount: number; lastAsked: string | null };
      }>(`/api/admin/questions/${questionId}/competitive-history`);
      if (res.data.success && res.data.data) {
        setQuestionCompetitiveHistory((prev) => ({
          ...prev,
          [questionId]: res.data.data
        }));
      }
    } catch (err: any) {
      console.error('[AdminContentManager] Failed to load competitive history', err);
    }
  }, []);

  const toggleSubtopicNiche = async (subtopic: SubtopicRow) => {
    const key = `${subtopic.main_topic}::${subtopic.subtopic}`;
    const nextValue = !subtopic.isNiche;
    setSavingNicheKey(key);
    try {
      const res = await axios.put(`/api/admin/subtopics/${encodeURIComponent(subtopic.subtopic)}/niche`, {
        isNiche: nextValue
      });
      if (!res.data.success) {
        throw new Error(res.data.message || 'Failed to update niche flag');
      }
      setTopics((prev) =>
        prev.map((topic) => ({
          ...topic,
          subtopics: topic.subtopics.map((s) =>
            s.subtopic === subtopic.subtopic && s.main_topic === subtopic.main_topic
              ? { ...s, isNiche: nextValue }
              : s
          )
        }))
      );
      showSuccess(`Marked "${subtopic.subtopic}" as ${nextValue ? 'niche' : 'standard'}.`, 2500);
    } catch (err: any) {
      console.error('[AdminContentManager] Failed to update niche flag', err);
      showError(err?.response?.data?.message || err?.message || 'Failed to update niche flag.', 4000);
    } finally {
      setSavingNicheKey(null);
    }
  };

  useEffect(() => {
    loadTopics();
    loadAnswerDistribution();
  }, [loadTopics, loadAnswerDistribution]);

  const distributionEntries = useMemo(() => {
    const counts = answerDistribution?.counts || { A: 0, B: 0, C: 0, D: 0 };
    const total = answerDistribution?.total || 0;
    return (['A', 'B', 'C', 'D'] as AnswerOption[]).map((key) => ({
      key,
      count: counts[key] || 0,
      percentage: total > 0 ? (counts[key] / total) * 100 : 0
    }));
  }, [answerDistribution]);

  const handleRedistributeAnswers = async () => {
    if (redistributeTarget !== 'random' && redistributeTarget === redistributeFrom) {
      showError('Target answer must differ from the current correct answer.', 3000);
      return;
    }

    const confirmMessage = `Redistribute ${redistributeLimit} question(s) from ${redistributeFrom} to ${redistributeTarget}. This swaps answer text and updates the correct answer. Continue?`;
    confirmAction(confirmMessage, async () => {
      setRedistributeLoading(true);
      try {
        const res = await axios.post<{
          success: boolean;
          data: {
            updatedCount: number;
            distribution: AnswerDistribution;
          };
          message?: string;
        }>('/api/admin/questions/redistribute-answers', {
          fromAnswer: redistributeFrom,
          targetAnswer: redistributeTarget,
          limit: redistributeLimit,
          includeActive: true
        });
        if (!res.data.success) throw new Error(res.data.message || 'Failed to redistribute answers');
        setAnswerDistribution(res.data.data.distribution);
        showSuccess(res.data.message || 'Answers redistributed.', 3000);
      } catch (err: any) {
        console.error('[AdminContentManager] Failed to redistribute answers', err);
        showError(err?.response?.data?.message || err?.message || 'Failed to redistribute answers.', 4000);
      } finally {
        setRedistributeLoading(false);
      }
    });
  };

  const toggleTopic = (mainTopic: string) => {
    setExpandedTopics((prev) => {
      const next = new Set(prev);
      if (next.has(mainTopic)) next.delete(mainTopic);
      else next.add(mainTopic);
      return next;
    });
  };

  const handleToggleSubtopic = (key: string, s: SubtopicRow) => {
    setExpandedSubtopics((prev) => {
      const next = new Set(prev);
      const willExpand = !next.has(key);
      if (willExpand) next.add(key);
      else next.delete(key);

      // Load questions when expanding (only if we haven't loaded yet)
      if (willExpand && !subtopicQuestions[key]?.questions?.length && !subtopicQuestions[key]?.loading) {
        void loadQuestionsForSubtopic(key, s.main_topic, s.subtopic, 1);
      }
      return next;
    });
  };

  const loadQuestionsForSubtopic = useCallback(
    async (key: string, mainTopic: string, category: string, page: number) => {
      setSubtopicQuestions((prev) => ({
        ...prev,
        [key]: {
          loading: true,
          error: null,
          page,
          pagination: prev[key]?.pagination ?? null,
          questions: prev[key]?.questions ?? []
        }
      }));
      try {
        const res = await axios.get<{
          success: boolean;
          data: { questions: AdminQuestion[]; pagination: Pagination };
          message?: string;
        }>('/api/admin/questions', {
          params: {
            page,
            limit: QUESTIONS_PAGE_SIZE,
            mainTopic,
            category
          }
        });
        if (!res.data.success) {
          throw new Error(res.data.message || 'Failed to load questions');
        }
        setSubtopicQuestions((prev) => ({
          ...prev,
          [key]: {
            loading: false,
            error: null,
            page,
            pagination: res.data.data.pagination,
            questions: res.data.data.questions || []
          }
        }));
      } catch (err: any) {
        console.error('[AdminContentManager] Failed to load questions for subtopic', { key, err });
        setSubtopicQuestions((prev) => ({
          ...prev,
          [key]: {
            loading: false,
            error: err?.response?.data?.message || err?.message || 'Failed to load questions.',
            page,
            pagination: prev[key]?.pagination ?? null,
            questions: prev[key]?.questions ?? []
          }
        }));
      }
    },
    []
  );

  const openCreateQuestion = (key: string, category: string) => {
    const [mainTopic] = key.split('::');
    setActiveQuestionKey(key);
    setEditingQuestion(null);
    setQuestionModalError(null);
    setQuestionForm({
      question_text: '',
      option_a: '',
      option_b: '',
      option_c: '',
      option_d: '',
      correct_answer: 'A',
      category,
      difficulty: 'hard'
    });
    setQuestionMainTopic(mainTopic);
    setQuestionMainTopicMode('existing');
    setQuestionCategoryMode('existing');
    setShowQuestionModal(true);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _openMoveSubtopic = (key: string, fromMainTopic: string, fromCategory: string) => {
    setMoveSubtopicKey(key);
    setMoveFromCategory(fromCategory);
    setMoveFromMainTopic(fromMainTopic);

    setMoveMainTopicMode('existing');
    setMoveSelectedMainTopic(fromMainTopic);
    setMoveNewMainTopic('');

    setMoveSubtopicMode('existing');
    setMoveSelectedSubtopic('');
    setMoveNewSubtopic('');

    setMoveSubtopicError(null);
    setShowMoveSubtopicModal(true);
  };

  const handleMoveSubtopic = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!moveSubtopicKey) return;

    const fromCategory = moveFromCategory.trim();
    const toMainTopic =
      moveMainTopicMode === 'existing' ? moveSelectedMainTopic.trim() : moveNewMainTopic.trim();
    const toCategory =
      moveSubtopicMode === 'existing' ? moveSelectedSubtopic.trim() : moveNewSubtopic.trim();
    if (!fromCategory || !toCategory) {
      setMoveSubtopicError('Both current and new subtopic are required.');
      return;
    }
    if (!toMainTopic) {
      setMoveSubtopicError('Destination main topic is required.');
      return;
    }

    setMovingSubtopic(true);
    setMoveSubtopicError(null);
    try {
      showInfo(`Moving all questions from "${fromCategory}" to "${toCategory}"…`, 2500);
      const res = await axios.post<{
        success: boolean;
        data?: { updatedCount: number };
        message?: string;
      }>('/api/admin/questions/bulk-move-category', { fromCategory, toCategory, toMainTopic });

      if (!res.data.success) throw new Error(res.data.message || 'Failed to move questions');

      setShowMoveSubtopicModal(false);

      // Refresh the overall topics list so counts update
      await loadTopics();

      // Refresh the question list for this panel if it's still expanded
      const [mainTopic] = moveSubtopicKey.split('::');
      const page = subtopicQuestions[moveSubtopicKey]?.page || 1;
      void loadQuestionsForSubtopic(moveSubtopicKey, mainTopic, toCategory, page);

      const updatedCount = res.data.data?.updatedCount ?? 0;
      showSuccess(`Moved ${updatedCount} question(s) to "${toCategory}".`, 3000);
    } catch (err: any) {
      console.error('[AdminContentManager] Bulk move failed', err);
      const msg = err?.response?.data?.message || err?.message || 'Failed to move questions.';
      setMoveSubtopicError(msg);
      showError(msg, 4500);
    } finally {
      setMovingSubtopic(false);
    }
  };

  const openEditQuestion = (key: string, q: AdminQuestion) => {
    setActiveQuestionKey(key);
    setEditingQuestion(q);
    setQuestionModalError(null);
    setQuestionForm({
      question_text: q.question_text,
      option_a: q.option_a,
      option_b: q.option_b,
      option_c: q.option_c,
      option_d: q.option_d,
      correct_answer: q.correct_answer,
      category: q.category,
      difficulty: q.difficulty
    });
    // Set main topic from question
    setQuestionMainTopic(q.main_topic || '');
    setQuestionMainTopicMode(q.main_topic ? 'existing' : 'new');
    setQuestionCategoryMode('existing');
    setShowQuestionModal(true);
  };

  const handleQuestionFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setQuestionForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    setQuestionSaving(true);
    setQuestionModalError(null);
    try {
      if (editingQuestion) {
        const res = await axios.put(`/api/admin/questions/${editingQuestion.id}`, {
          questionText: questionForm.question_text,
          optionA: questionForm.option_a,
          optionB: questionForm.option_b,
          optionC: questionForm.option_c,
          optionD: questionForm.option_d,
          correctAnswer: questionForm.correct_answer,
          category: questionForm.category,
          difficulty: questionForm.difficulty
        });
        if (!res.data.success) throw new Error(res.data.message || 'Failed to update question');
      } else {
        const res = await axios.post('/api/admin/questions', {
          questionText: questionForm.question_text,
          optionA: questionForm.option_a,
          optionB: questionForm.option_b,
          optionC: questionForm.option_c,
          optionD: questionForm.option_d,
          correctAnswer: questionForm.correct_answer,
          category: questionForm.category,
          difficulty: questionForm.difficulty
        });
        if (!res.data.success) throw new Error(res.data.message || 'Failed to create question');
      }

      setShowQuestionModal(false);
      setEditingQuestion(null);

      // Refresh the active subtopic list
      if (activeQuestionKey) {
        const state = subtopicQuestions[activeQuestionKey];
        // We need mainTopic/category to reload; infer from key and form/category.
        const [mainTopic] = activeQuestionKey.split('::');
        const category = questionForm.category;
        const nextPage = state?.page || 1;
        void loadQuestionsForSubtopic(activeQuestionKey, mainTopic, category, nextPage);
      }
    } catch (err: any) {
      console.error('[AdminContentManager] Failed to save question', err);
      setQuestionModalError(
        err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to save question.'
      );
    } finally {
      setQuestionSaving(false);
    }
  };

  const handleDeleteQuestion = async (key: string, q: AdminQuestion) => {
    confirmAction(
      'Delete this question? This cannot be undone.',
      async () => {
        try {
          await axios.delete(`/api/admin/questions/${q.id}`, {
            headers: { 'X-Confirm-Action': 'DELETE_QUESTION' }
          });
          const [mainTopic] = key.split('::');
          const state = subtopicQuestions[key];
          const page = state?.page || 1;
          void loadQuestionsForSubtopic(key, mainTopic, q.category, page);
          showSuccess('Question deleted successfully.', 3000);
        } catch (err: any) {
          console.error('[AdminContentManager] Failed to delete question', err);
          showError(err?.response?.data?.message || 'Failed to delete question.', 4500);
        }
      }
    );
  };

  const handleDeleteTopic = async (topicName: string, questionCount: number) => {
    const message = questionCount > 0
      ? `Delete topic "${topicName}"? This will permanently delete ${questionCount} question(s) and all subtopics. This cannot be undone.`
      : `Delete topic "${topicName}"? This will permanently delete the topic and all its subtopics. This cannot be undone.`;
    
    confirmAction(message, async () => {
      try {
        const token = await ensureCsrfToken();
        const headers: Record<string, string> = { 'X-Confirm-Action': 'DELETE_TOPIC' };
        if (token) headers['X-CSRF-Token'] = token;
        try {
          await axios.delete(`/api/admin/topics/${encodeURIComponent(topicName)}`, { headers });
        } catch (e: any) {
          if (e?.response?.status === 403 && e?.response?.data?.code?.includes?.('CSRF')) {
            const retryToken = await ensureCsrfToken(true);
            if (retryToken) headers['X-CSRF-Token'] = retryToken;
            await axios.delete(`/api/admin/topics/${encodeURIComponent(topicName)}`, { headers });
          } else throw e;
        }
        showSuccess(`Topic "${topicName}" deleted successfully.`, 3000);
        await loadTopics();
      } catch (err: any) {
        console.error('[AdminContentManager] Failed to delete topic', err);
        showError(err?.response?.data?.message || 'Failed to delete topic.', 4500);
      }
    });
  };

  const handleDeleteSubtopic = async (mainTopic: string, subtopicName: string, questionCount: number) => {
    const message = questionCount > 0
      ? `Delete subtopic "${subtopicName}"? This will permanently delete ${questionCount} question(s). This cannot be undone.`
      : `Delete subtopic "${subtopicName}"? This cannot be undone.`;
    
    confirmAction(message, async () => {
      try {
        const token = await ensureCsrfToken();
        const headers: Record<string, string> = { 'X-Confirm-Action': 'DELETE_SUBTOPIC' };
        if (token) headers['X-CSRF-Token'] = token;
        try {
          await axios.delete(`/api/admin/subtopics/${encodeURIComponent(subtopicName)}?mainTopic=${encodeURIComponent(mainTopic)}`, {
            headers
          });
        } catch (e: any) {
          if (e?.response?.status === 403 && e?.response?.data?.code?.includes?.('CSRF')) {
            const retryToken = await ensureCsrfToken(true);
            if (retryToken) headers['X-CSRF-Token'] = retryToken;
            await axios.delete(`/api/admin/subtopics/${encodeURIComponent(subtopicName)}?mainTopic=${encodeURIComponent(mainTopic)}`, {
              headers
            });
          } else throw e;
        }
        showSuccess(`Subtopic "${subtopicName}" deleted successfully.`, 3000);
        await loadTopics();
      } catch (err: any) {
        console.error('[AdminContentManager] Failed to delete subtopic', err);
        showError(err?.response?.data?.message || 'Failed to delete subtopic.', 4500);
      }
    });
  };

  const handleGenerateSubtopic = async (s: SubtopicRow) => {
    const key = `${s.main_topic}::${s.subtopic}`;
    setGeneratingSubtopic(key);
    
    // Use default distribution: 30 hard, 15 medium, 5 easy
    showInfo(`Generating 50 questions (30 hard, 15 medium, 5 easy) for ${s.subtopic}...`, 3000);
    
    try {
      const res = await axios.post<{
        success: boolean;
        data?: { generated: number; candidateIds: string[]; duplicatesDeleted?: number };
        message?: string;
      }>('/api/admin/subtopics/ai-generate', {
        mainTopic: s.main_topic,
        subtopic: s.subtopic,
        difficultyDistribution: { hard: 30, medium: 15, easy: 5 }
      });
      await loadTopics();
      
      // Show notification when job completes
      const generatedCount = res.data.data?.generated ?? 0;
      const duplicatesDeleted = res.data.data?.duplicatesDeleted ?? 0;
      if (generatedCount > 0) {
        let message = `${generatedCount} question(s) generated for ${s.main_topic} • ${s.subtopic} and ready for review.`;
        if (duplicatesDeleted > 0) {
          message += ` ${duplicatesDeleted} duplicate(s) auto-deleted.`;
        }
        showSuccess(message, 5000);
      } else {
        showSuccess(`Generation completed for ${s.main_topic} • ${s.subtopic}.`, 3000);
      }
    } catch (err: any) {
      console.error('[AdminContentManager] Generation failed', err);
      showError(err?.response?.data?.message || 'Failed to generate candidates.', 4500);
    } finally {
      setGeneratingSubtopic(null);
    }
  };

  // Editing handlers
  const openEditTopic = (mainTopic: string) => {
    setEditingTopicName(mainTopic);
    setEditingTopicKey(mainTopic);
    setEditError(null);
    setShowEditTopicModal(true);
  };

  const openEditSubtopic = (mainTopic: string, subtopic: string) => {
    setEditingSubtopicName(subtopic);
    setEditingSubtopicMainTopic(mainTopic);
    setEditingSubtopicMainTopicMode('existing');
    setEditingSubtopicNameMode('existing');
    setEditingSubtopicKey(`${mainTopic}::${subtopic}`);
    setEditError(null);
    setShowEditSubtopicModal(true);
  };

  const handleSaveTopicEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTopicKey || !editingTopicName.trim()) return;
    
    setSavingEdit(true);
    setEditError(null);
    try {
      const res = await axios.put(`/api/admin/topics/${encodeURIComponent(editingTopicKey)}`, {
        newName: editingTopicName.trim()
      });
      if (!res.data.success) throw new Error(res.data.message || 'Failed to rename topic');
      showSuccess(`Topic renamed to "${editingTopicName.trim()}".`, 3000);
      await loadTopics();
      setShowEditTopicModal(false);
    } catch (err: any) {
      console.error('[AdminContentManager] Failed to rename topic', err);
      setEditError(err?.response?.data?.message || 'Failed to rename topic.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSaveSubtopicEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSubtopicKey) return;
    
    const mainTopicValue = editingSubtopicMainTopicMode === 'existing' 
      ? editingSubtopicMainTopic.trim() 
      : editingSubtopicMainTopic.trim();
    const subtopicValue = editingSubtopicNameMode === 'existing'
      ? editingSubtopicName.trim()
      : editingSubtopicName.trim();
    
    if (!mainTopicValue || !subtopicValue) {
      setEditError('Both main topic and subtopic are required.');
      return;
    }
    
    setSavingEdit(true);
    setEditError(null);
    try {
      const originalSubtopic = editingSubtopicKey.split('::')[1];
      const res = await axios.put(`/api/admin/subtopics/${encodeURIComponent(originalSubtopic)}`, {
        newName: subtopicValue,
        mainTopic: mainTopicValue
      });
      if (!res.data.success) throw new Error(res.data.message || 'Failed to rename subtopic');
      showSuccess(`Subtopic updated.`, 3000);
      await loadTopics();
      setShowEditSubtopicModal(false);
    } catch (err: any) {
      console.error('[AdminContentManager] Failed to rename subtopic', err);
      setEditError(err?.response?.data?.message || 'Failed to rename subtopic.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleGenerateBulk = async () => {
    setGeneratingBulk(true);
    showInfo('Generating 500 candidate questions… This may take several minutes.', 5000);
    try {
      const res = await axios.post<{
        success: boolean;
        data?: { totalCandidatesCreated: number; rounds: number; message: string };
        message?: string;
      }>('/api/admin/generate-bulk', { targetCount: 500 }, { timeout: 20 * 60 * 1000 });
      if (!res.data.success) throw new Error(res.data.message || 'Failed to generate');
      const count = res.data.data?.totalCandidatesCreated ?? 0;
      showSuccess(res.data.data?.message || `Generated ${count} candidates. Approve in Question Review.`, 8000);
      await loadTopics();
    } catch (err: any) {
      showError(err?.response?.data?.message || 'Bulk generation failed.', 5000);
    } finally {
      setGeneratingBulk(false);
    }
  };

  const handleGenerateTopic = async (mainTopic: string) => {
    setGeneratingTopic(mainTopic);
    
    showInfo(`Queuing generation job for ${mainTopic}...`, 3000);
    
    try {
      const res = await axios.post<{
        success: boolean;
        data?: { 
          jobId?: string;
          generated?: number; 
          candidateIds?: string[]; 
          duplicatesDeleted?: number;
          message?: string;
          statusEndpoint?: string;
        };
        message?: string;
      }>('/api/admin/subtopics/ai-generate', {
        mainTopic,
        difficultyDistribution: { hard: 30, medium: 15, easy: 5 }
      });

      // Check if we got a job ID (new async format) or direct results (old format)
      if (res.data.data?.jobId) {
        const jobId = res.data.data.jobId;
        showInfo(`Generation job queued. Monitoring progress...`, 5000);
        
        // Poll for job status
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await axios.get<{
              success: boolean;
              data?: {
                id: string;
                status: 'queued' | 'running' | 'succeeded' | 'failed';
                totalCandidatesCreated: number | null;
                errorMessage: string | null;
                startedAt: string | null;
                finishedAt: string | null;
              };
            }>(`/api/admin/jobs/${jobId}`);
            
            const job = statusRes.data.data;
            if (!job) {
              clearInterval(pollInterval);
              showError('Job not found', 3000);
              setGeneratingTopic(null);
              return;
            }

            // Update progress message
            if (job.status === 'running' && job.totalCandidatesCreated !== null) {
              showInfo(`Generating... ${job.totalCandidatesCreated} questions created so far`, 2000);
            }

            if (job.status === 'succeeded') {
              clearInterval(pollInterval);
              await loadTopics();
              const count = job.totalCandidatesCreated || 0;
              if (count > 0) {
                showSuccess(`${count} question(s) generated for ${mainTopic} and ready for review.`, 5000);
              } else {
                showSuccess(`Generation completed for ${mainTopic}.`, 3000);
              }
              setGeneratingTopic(null);
            } else if (job.status === 'failed') {
              clearInterval(pollInterval);
              showError(job.errorMessage || 'Generation failed', 5000);
              setGeneratingTopic(null);
            }
          } catch (pollErr: any) {
            console.error('[AdminContentManager] Error polling job status', pollErr);
            // Don't clear interval on network errors, keep trying
          }
        }, 2000); // Poll every 2 seconds

        // Timeout after 10 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          if (generatingTopic === mainTopic) {
            showError('Generation timed out. Check job status manually.', 5000);
            setGeneratingTopic(null);
          }
        }, 10 * 60 * 1000);
      } else {
        // Old format - direct results (backward compatibility)
        await loadTopics();
        const generatedCount = res.data.data?.generated ?? 0;
        const duplicatesDeleted = res.data.data?.duplicatesDeleted ?? 0;
        if (generatedCount > 0) {
          let message = `${generatedCount} question(s) generated for ${mainTopic} and ready for review.`;
          if (duplicatesDeleted > 0) {
            message += ` ${duplicatesDeleted} duplicate(s) auto-deleted.`;
          }
          showSuccess(message, 5000);
        } else {
          showSuccess(`Generation completed for ${mainTopic}.`, 3000);
        }
        setGeneratingTopic(null);
      }
    } catch (err: any) {
      console.error('[AdminContentManager] Topic generation failed', err);
      showError(err?.response?.data?.message || 'Failed to generate candidates.', 4500);
      setGeneratingTopic(null);
    }
  };

  const openCreateTopic = () => {
    setCreateTopicMessage(null);
    setCreateTopicError(null);
    setCreateTopicMain('');
    setCreateTopicMainMode('existing');
    setCreateTopicSubtopic('');
    setShowAddAnother(false);
    setShowCreateTopicModal(true);
  };

  const handleCreateTopic = async (e: React.FormEvent) => {
    e.preventDefault();
    const mainTopicValue = createTopicMainMode === 'existing' ? createTopicMain.trim() : createTopicMain.trim();
    if (!mainTopicValue || !createTopicSubtopic.trim()) {
      setCreateTopicError('Both main topic and subtopic are required.');
      return;
    }
    setCreatingTopic(true);
    setCreateTopicMessage(null);
    setCreateTopicError(null);
    try {
      const res = await axios.post('/api/admin/topics/create', {
        mainTopic: mainTopicValue,
        subtopic: createTopicSubtopic.trim()
      });
      if (!res.data.success) throw new Error(res.data.message || 'Failed to create topic');
      setCreateTopicMessage('Topic created. Candidate questions have been queued for review.');
      await loadTopics();
      // Clear only subtopic, keep main topic for "add another" behavior
      setCreateTopicSubtopic('');
      setShowAddAnother(true);
      // Don't close modal - allow "Add Another"
    } catch (err: any) {
      console.error('[AdminContentManager] Create topic failed', err);
      setCreateTopicError(err?.response?.data?.message || 'Failed to create topic.');
    } finally {
      setCreatingTopic(false);
    }
  };

  const handleAddAnother = () => {
    setCreateTopicMessage(null);
    setCreateTopicError(null);
    // Keep main topic, only clear subtopic (as per requirements)
    setCreateTopicSubtopic('');
    setShowAddAnother(false);
  };

  const openSuggestionsModal = () => {
    setShowSuggestionsModal(true);
    setSuggestionsError(null);
    setSuggestions([]); // Clear previous suggestions when opening modal
  };

  const handleLoadSuggestions = useCallback(async (e?: React.MouseEvent) => {
    // Prevent event propagation and default behavior
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Prevent multiple simultaneous requests
    // Use functional update to atomically check and set loading state
    let canProceed = false;
    setLoadingSuggestions((prev) => {
      if (prev) {
        return prev; // Already loading, keep it true
      }
      canProceed = true;
      return true; // Start loading
    });
    
    if (!canProceed) {
      return; // Already loading, exit early
    }
    
    // Clear previous suggestions and errors before making new request
    setSuggestions([]);
    setSuggestionsError(null);
    
    // Use the latest state values from the callback dependencies
    const filterValue = suggestionMainTopicFilter;
    const reviewValue = suggestionReviewExisting;
    const instructionsValue = suggestionCustomInstructions;
    
    try {
      const res = await axios.post<{
        success: boolean;
        data?: { suggestions?: TopicSuggestionItem[] };
        error?: string;
        message?: string;
      }>('/api/admin/topics/suggest', {
        count: 10,
        mainTopicFilter: filterValue || undefined,
        reviewExisting: reviewValue,
        customInstructions: instructionsValue || undefined
      });
      
      if (!res.data.success) {
        throw new Error(res.data.error || res.data.message || 'Failed to load suggestions');
      }
      
      // Safely extract suggestions from response
      const suggestions = res.data.data?.suggestions || [];
      setSuggestions(suggestions);
      
      // Clear any previous errors on success
      setSuggestionsError(null);
    } catch (err: any) {
      console.error('[AdminContentManager] Failed to load suggestions', err);
      const errorMessage = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to load suggestions.';
      setSuggestionsError(errorMessage);
      // Clear suggestions on error
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [suggestionMainTopicFilter, suggestionReviewExisting, suggestionCustomInstructions]);

  const acceptSuggestion = async (s: TopicSuggestionItem) => {
    const key = `${s.topicName}::${s.subtopicName}`;
    setAcceptingSuggestion(key);
    try {
      const res = await axios.post('/api/admin/topics/create', {
        mainTopic: s.topicName,
        subtopic: s.subtopicName
      });
      if (!res.data.success) throw new Error(res.data.message || 'Failed to create topic');
      setSuggestions((prev) => prev.filter((x) => `${x.topicName}::${x.subtopicName}` !== key));
      await loadTopics();
    } catch (err: any) {
      console.error('[AdminContentManager] Accept suggestion failed', err);
      showError(err?.response?.data?.message || 'Failed to create topic from suggestion.', 4500);
    } finally {
      setAcceptingSuggestion(null);
    }
  };

  const handleLoadDuplicates = async () => {
    setShowDuplicateModal(true);
    setDuplicatesError(null);
    setLoadingDuplicates(true);
    setDuplicateGroups([]);
    setTotalDuplicates(0);
    try {
      const res = await axios.get<{
        success: boolean;
        data: {
          duplicateGroups: Array<{
            keepId: string;
            duplicates: Array<{
              id: string;
              question_text: string;
              category: string;
              difficulty: string;
              created_at: string;
            }>;
          }>;
          totalGroups: number;
          totalDuplicates: number;
          similarityThreshold: number;
        };
        message?: string;
      }>('/api/admin/questions/duplicates');
      if (!res.data.success) throw new Error(res.data.message || 'Failed to load duplicates');
      setDuplicateGroups(res.data.data.duplicateGroups || []);
      setTotalDuplicates(res.data.data.totalDuplicates || 0);
    } catch (err: any) {
      console.error('[AdminContentManager] Failed to load duplicates', err);
      setDuplicatesError(err?.response?.data?.message || err?.message || 'Failed to load duplicates.');
    } finally {
      setLoadingDuplicates(false);
    }
  };

  const handleCleanupDuplicates = () => {
    if (totalDuplicates === 0) {
      showInfo('No duplicates to clean up.', 2000);
      return;
    }

    confirmAction(
      `Are you sure you want to delete ${totalDuplicates} duplicate question(s)? This action cannot be undone.`,
      async () => {
        setCleaningDuplicates(true);
        const duplicatesBeforeCleanup = totalDuplicates;
        showInfo('Duplicate cleanup job started. This may take a few moments...', 3000);

        try {
          const res = await axios.post<{
            success: boolean;
            message?: string;
          }>('/api/admin/questions/duplicates/cleanup');

          if (!res.data.success) throw new Error(res.data.message || 'Failed to start cleanup');

          // Poll for completion - check every 2 seconds, up to 10 times (20 seconds max)
          let pollCount = 0;
          const maxPolls = 10;
          const pollInterval = setInterval(async () => {
            pollCount++;
            try {
              const checkRes = await axios.get<{
                success: boolean;
                data: {
                  totalDuplicates: number;
                };
              }>('/api/admin/questions/duplicates');
              
              if (checkRes.data.success) {
                const remainingDuplicates = checkRes.data.data.totalDuplicates;
                if (remainingDuplicates === 0 || pollCount >= maxPolls) {
                  clearInterval(pollInterval);
                  await handleLoadDuplicates();
                  const deletedCount = duplicatesBeforeCleanup - remainingDuplicates;
                  showSuccess(`Duplicate cleanup completed. ${deletedCount} duplicate question(s) deleted.`, 4000);
                  setCleaningDuplicates(false);
                  if (remainingDuplicates === 0) {
                    setTimeout(() => setShowDuplicateModal(false), 1500);
                  }
                }
              }
            } catch (err: any) {
              // If polling fails, just refresh once and show message
              if (pollCount >= maxPolls) {
                clearInterval(pollInterval);
                try {
                  await handleLoadDuplicates();
                  showSuccess('Duplicate cleanup job completed.', 4000);
                } catch (refreshErr) {
                  showError('Cleanup job completed, but failed to refresh duplicate list.', 4000);
                }
                setCleaningDuplicates(false);
              }
            }
          }, 2000);
        } catch (err: any) {
          console.error('[AdminContentManager] Cleanup failed', err);
          showError(err?.response?.data?.message || 'Failed to start cleanup job.', 4500);
          setCleaningDuplicates(false);
        }
      }
    );
  };

  return (
    <div className="admin-page">
      <SEOHead title="Admin | Content Manager" description="Manage topics, subtopics, and question generation." />
      <header className="admin-page__header">
        <h1 className="admin-page__title">Content</h1>
        <p className="admin-page__subtitle">Topics, subtopics, and question generation.</p>
      </header>

        <section className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <h2>Answer distribution</h2>
              <p>Visual check of correct answer balance across A, B, C, D.</p>
            </div>
            <button
              type="button"
              className="btn btn-secondary btn-xs"
              onClick={loadAnswerDistribution}
              disabled={distributionLoading}
            >
              {distributionLoading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
          {distributionError ? (
            <div className="admin-error">
              <strong>Error:</strong> {distributionError}
            </div>
          ) : (
            <div>
              <div>
                {distributionEntries.map((entry) => (
                  <div key={entry.key} className="admin-distribution-row">
                    <div style={{ minWidth: 48 }}>
                      <span style={{ fontWeight: 'var(--font-weight-semibold)' }}>{entry.key}</span>
                      <span style={{ marginLeft: 'var(--space-1)', color: 'var(--color-text-muted)' }}>{entry.count}</span>
                    </div>
                    <div className="admin-distribution-bar">
                      <div
                        className="admin-distribution-bar-fill"
                        style={{ width: `${entry.percentage}%` }}
                      />
                    </div>
                    <span style={{ minWidth: 48, textAlign: 'right', fontSize: 'var(--font-size-sm)' }}>
                      {entry.percentage.toFixed(1)}%
                    </span>
                  </div>
                ))}
                <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--color-border)', fontSize: 'var(--font-size-sm)' }}>
                  Total questions: {answerDistribution?.total ?? 0}
                </div>
              </div>

              <div style={{ marginTop: 'var(--space-4)' }}>
                <div className="admin-card__label" style={{ marginBottom: 'var(--space-2)' }}>Redistribute answers</div>
                <p style={{ margin: '0 0 var(--space-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                  Swaps the correct option with a new letter.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', alignItems: 'center' }}>
                  <label>
                    From
                    <select value={redistributeFrom} onChange={(e) => setRedistributeFrom(e.target.value as AnswerOption)}>
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                      <option value="D">D</option>
                    </select>
                  </label>
                  <label>
                    To
                    <select value={redistributeTarget} onChange={(e) => setRedistributeTarget(e.target.value as AnswerOption | 'random')}>
                      <option value="random">Random (B/C/D)</option>
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                      <option value="D">D</option>
                    </select>
                  </label>
                  <label>
                    Count
                    <input
                      type="number"
                      min={1}
                      max={1000}
                      value={redistributeLimit}
                      onChange={(e) => setRedistributeLimit(Number(e.target.value))}
                    />
                  </label>
                  <button
                    type="button"
                    className="btn btn-primary btn-xs"
                    onClick={handleRedistributeAnswers}
                    disabled={redistributeLoading || !answerDistribution?.total || redistributeLimit < 1}
                  >
                    {redistributeLoading ? 'Redistributing…' : 'Redistribute'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>

        <div className="admin-page__controls">
          <button type="button" className="btn btn-primary btn-xs" onClick={openCreateTopic}>
            + Add Topic
          </button>
          <button
            type="button"
            className="btn btn-primary btn-xs"
            onClick={handleGenerateBulk}
            disabled={generatingBulk}
            title="Generate ~500 candidate questions for topics below target. Approve in Question Review."
          >
            {generatingBulk ? 'Generating…' : 'Generate 500'}
          </button>
          <button type="button" className="btn btn-secondary btn-xs" onClick={openSuggestionsModal}>
            AI Topic Suggestions
          </button>
          <button type="button" className="btn btn-secondary btn-xs" onClick={() => handleLoadDuplicates()}>
            Clean Up Duplicates
          </button>
          <span className="admin-page__meta" title="Active generation jobs">
            {processingJobs} processing
          </span>

          <select value={filterMainTopic} onChange={(e) => setFilterMainTopic(e.target.value)}>
            <option value="">All main topics</option>
            {mainTopics.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>

          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={onlyBelowTarget}
              onChange={(e) => setOnlyBelowTarget(e.target.checked)}
            />{' '}
            Only below target
          </label>

          <input
            className="admin-page__search"
            placeholder="Search topics/subtopics/questions…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <button type="button" className="btn btn-secondary btn-xs" onClick={loadTopics} disabled={loading}>
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="admin-loading">Loading…</div>
        ) : error ? (
          <div className="admin-error">
            <strong>Error:</strong> {error}
          </div>
        ) : topics.length === 0 ? (
          <p className="admin-loading">No topics found.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-content-table">
              <thead>
                <tr>
                  <th style={{ width: '32px' }}></th>
                  <th>Topic / Subtopic</th>
                  <th>Questions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {topics.map((t) => {
                  const expanded = expandedTopics.has(t.mainTopic);
                  const totalQuestions = t.subtopics.reduce((sum, s) => sum + s.total_questions, 0);
                  return (
                    <React.Fragment key={t.mainTopic}>
                      <tr className="admin-content-topic-row">
                        <td>
                          <button
                            type="button"
                            className="admin-content-topic__chev-btn"
                            onClick={() => toggleTopic(t.mainTopic)}
                            aria-label={expanded ? 'Collapse topic' : 'Expand topic'}
                          >
                            {expanded ? '▾' : '▸'}
                          </button>
                        </td>
                        <td data-label="Topic / Subtopic">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <strong>{t.mainTopic}</strong>
                            <button
                              type="button"
                              className="btn btn-ghost btn-xs"
                              onClick={() => openEditTopic(t.mainTopic)}
                              title="Edit topic"
                              style={{ padding: '0.25rem', minWidth: 'auto', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="btn btn-ghost btn-xs"
                              onClick={() => handleDeleteTopic(t.mainTopic, totalQuestions)}
                              title="Delete topic"
                              style={{ padding: '0.25rem', minWidth: 'auto', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                              </svg>
                            </button>
                          </div>
                        </td>
                        <td data-label="Questions">
                          {totalQuestions} total ({t.subtopicCount} subtopics)
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-primary btn-xs"
                            onClick={() => handleGenerateTopic(t.mainTopic)}
                            disabled={generatingTopic === t.mainTopic}
                          >
                            {generatingTopic === t.mainTopic ? 'Generating…' : 'Generate'}
                          </button>
                        </td>
                      </tr>

                      {expanded && t.subtopics.map((s) => {
                        const key = `${t.mainTopic}::${s.subtopic}`;
                        const expandedSub = expandedSubtopics.has(key);
                        return (
                          <React.Fragment key={s.id}>
                            <tr className="admin-content-subtopic-row">
                              <td>
                                <button
                                  type="button"
                                  className="admin-content-subtopic__toggle"
                                  onClick={() => handleToggleSubtopic(key, s)}
                                  aria-label={expandedSub ? 'Collapse subtopic' : 'Expand subtopic'}
                                >
                                  {expandedSub ? '▾' : '▸'}
                                </button>
                              </td>
                              <td data-label="Topic / Subtopic">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', paddingLeft: '1.5rem' }}>
                                  <strong>{s.subtopic}</strong>
                                  {s.isNiche && <span className="admin-content-subtopic__badge">Niche</span>}
                                  <button
                                    type="button"
                                    className="btn btn-ghost btn-xs"
                                    onClick={() => openEditSubtopic(s.main_topic, s.subtopic)}
                                    title="Edit subtopic"
                                    style={{ padding: '0.25rem', minWidth: 'auto', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                    </svg>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-ghost btn-xs"
                                    onClick={() => handleDeleteSubtopic(s.main_topic, s.subtopic, s.total_questions)}
                                    title="Delete subtopic"
                                    style={{ padding: '0.25rem', minWidth: 'auto', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="3 6 5 6 21 6"></polyline>
                                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                      <line x1="10" y1="11" x2="10" y2="17"></line>
                                      <line x1="14" y1="11" x2="14" y2="17"></line>
                                    </svg>
                                  </button>
                                </div>
                                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted, #6b7280)', paddingLeft: '1.5rem', marginTop: '0.2rem' }}>
                                  E: {s.easy_count}, M: {s.medium_count}, H: {s.hard_count}
                                </div>
                              </td>
                              <td data-label="Questions">{s.total_questions}</td>
                              <td>
                                <button
                                  type="button"
                                  className={`btn btn-ghost btn-xs admin-content-subtopic__niche-toggle${s.isNiche ? ' is-active' : ''}`}
                                  onClick={() => toggleSubtopicNiche(s)}
                                  disabled={savingNicheKey === key}
                                >
                                  {savingNicheKey === key ? 'Saving…' : s.isNiche ? 'Niche' : 'Standard'}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-primary btn-xs"
                                  onClick={() => handleGenerateSubtopic(s)}
                                  disabled={generatingSubtopic === key}
                                >
                                  {generatingSubtopic === key ? 'Generating…' : 'Generate'}
                                </button>
                              </td>
                            </tr>

                            {expandedSub && (
                              <tr>
                                <td colSpan={5} style={{ padding: '0.75rem 1rem 1rem', background: 'var(--surface-subtle, #f9fafb)' }}>
                                  <div className="admin-content-subtopic__expanded">
                                    <div className="admin-content-subtopic__expanded-title">Questions</div>
                                <div className="admin-content-subtopic__expanded-footer">
                                  <div className="admin-content-subtopic__hint">
                                    Showing questions for <strong>{s.subtopic}</strong> (50 per page).
                                  </div>
                                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                    <button
                                      type="button"
                                      className="btn btn-secondary btn-xs"
                                      onClick={() => openCreateQuestion(key, s.subtopic)}
                                    >
                                      + Add question
                                    </button>
                                  </div>
                                </div>

                                {(() => {
                                  const state = subtopicQuestions[key];
                                  const pageToLoad = state?.page || 1;
                                  const pagination = state?.pagination;
                                  const totalPages = pagination?.totalPages || 1;
                                  const questions = state?.questions || [];
                                  const isLoading = state?.loading;
                                  const errMsg = state?.error;

                                  if (isLoading && questions.length === 0) {
                                    return <div className="admin-loading">Loading questions…</div>;
                                  }
                                  if (errMsg) {
                                    return (
                                      <div className="admin-loading">
                                        <strong>Error:</strong> {errMsg}
                                      </div>
                                    );
                                  }
                                  if (questions.length === 0) {
                                    return <div className="admin-loading">No questions found for this subtopic.</div>;
                                  }

                                  return (
                                    <>
                                      <div className="admin-cards">
                                        {questions.map((q) => {
                                          const difficultyClass =
                                            q.difficulty === 'easy'
                                              ? 'admin-card__badge admin-card__badge--easy'
                                              : q.difficulty === 'medium'
                                                ? 'admin-card__badge admin-card__badge--medium'
                                                : 'admin-card__badge admin-card__badge--hard';
                                          const used = Boolean(q.last_asked_date || q.date_active);
                                          return (
                                            <div key={q.id} className="admin-card">
                                              <div className="admin-card__header">
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                  <div className="admin-card__badges">
                                                    <span className={difficultyClass}>{q.difficulty}</span>
                                                    {used && (
                                                      <span className="admin-card__badge admin-card__badge--used">
                                                        Used
                                                      </span>
                                                    )}
                                                  </div>
                                                  <div className="admin-card__text">{q.question_text}</div>
                                                  <div className="admin-card__meta">
                                                    Updated {new Date(q.updated_at).toLocaleDateString()}
                                                    {q.last_asked_date
                                                      ? ` · Last asked ${new Date(q.last_asked_date).toLocaleDateString()}`
                                                      : ''}
                                                  </div>
                                                  {(() => {
                                                    const history = questionCompetitiveHistory[q.id];
                                                    if (history) {
                                                      return (
                                                        <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--text-muted, #6b7280)' }}>
                                                          <strong>Competitive:</strong> Used {history.totalCount} time(s)
                                                          {history.dates.length > 0 && (
                                                            <details style={{ marginTop: '0.25rem' }}>
                                                              <summary style={{ cursor: 'pointer', textDecoration: 'underline' }}>
                                                                View dates ({history.dates.length})
                                                              </summary>
                                                              <div style={{ marginTop: '0.25rem', paddingLeft: '1rem' }}>
                                                                {history.dates.map((date, idx) => (
                                                                  <div key={idx}>{new Date(date).toLocaleDateString()}</div>
                                                                ))}
                                                              </div>
                                                            </details>
                                                          )}
                                                        </div>
                                                      );
                                                    }
                                                    return null;
                                                  })()}
                                                  {!questionCompetitiveHistory[q.id] && (
                                                    <button
                                                      type="button"
                                                      className="btn btn-ghost btn-xs"
                                                      style={{ marginTop: '0.25rem', fontSize: '0.75rem' }}
                                                      onClick={() => loadQuestionCompetitiveHistory(q.id)}
                                                    >
                                                      Load competitive history
                                                    </button>
                                                  )}
                                                </div>
                                                <div className="admin-card__actions">
                                                  <button
                                                    type="button"
                                                    className="btn btn-secondary btn-xs"
                                                    onClick={() => openEditQuestion(key, q)}
                                                    title="Edit question"
                                                    style={{ padding: '0.375rem', minWidth: 'auto', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                  >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                                    </svg>
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className="btn btn-ghost btn-xs"
                                                    onClick={() => void handleDeleteQuestion(key, q)}
                                                    title="Delete question"
                                                    style={{ padding: '0.375rem', minWidth: 'auto', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                  >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                      <polyline points="3 6 5 6 21 6"></polyline>
                                                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                      <line x1="10" y1="11" x2="10" y2="17"></line>
                                                      <line x1="14" y1="11" x2="14" y2="17"></line>
                                                    </svg>
                                                  </button>
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>

                                      <div className="admin-cards-pagination">
                                        <button
                                          type="button"
                                          className="btn btn-secondary btn-xs"
                                          disabled={pageToLoad <= 1 || isLoading}
                                          onClick={() => void loadQuestionsForSubtopic(key, s.main_topic, s.subtopic, pageToLoad - 1)}
                                        >
                                          Previous
                                        </button>
                                        <div className="admin-cards-pagination__label">
                                          Page {pageToLoad} of {totalPages}
                                        </div>
                                        <button
                                          type="button"
                                          className="btn btn-secondary btn-xs"
                                          disabled={pageToLoad >= totalPages || isLoading}
                                          onClick={() => void loadQuestionsForSubtopic(key, s.main_topic, s.subtopic, pageToLoad + 1)}
                                        >
                                          Next
                                        </button>
                                      </div>
                                    </>
                                  );
                                })()}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      {/* Create Topic Modal */}
      {showCreateTopicModal && (
        <div className="admin-modal-overlay" onClick={() => setShowCreateTopicModal(false)}>
          <div className="admin-drawer" onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '1rem 1rem 0.5rem' }}>
              <h2 style={{ margin: 0 }}>Add Topic</h2>
              <p style={{ margin: '0.25rem 0 0', color: 'var(--text-muted, #6b7280)' }}>
                Creates a new (main topic, subtopic) and queues candidate generation.
              </p>
            </div>
            <div style={{ padding: '1rem' }}>
              {createTopicMessage && <div className="admin-loading">{createTopicMessage}</div>}
              {createTopicError && (
                <div className="admin-error">
                  <strong>Error:</strong> {createTopicError}
                </div>
              )}
              <form onSubmit={handleCreateTopic} style={{ display: 'grid', gap: '0.75rem' }}>
                <label>
                  Main topic
                  <select
                    value={createTopicMainMode === 'existing' ? createTopicMain : '__new__'}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '__new__') {
                        setCreateTopicMainMode('new');
                        setCreateTopicMain('');
                      } else {
                        setCreateTopicMainMode('existing');
                        setCreateTopicMain(val);
                      }
                    }}
                    required
                  >
                    <option value="">Select a main topic…</option>
                    {mainTopics.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                    <option value="__new__">Create new main topic…</option>
                  </select>
                </label>
                {createTopicMainMode === 'new' && (
                  <label>
                    New main topic name
                    <input
                      value={createTopicMain}
                      onChange={(e) => setCreateTopicMain(e.target.value)}
                      placeholder="Enter new main topic name…"
                      required
                    />
                  </label>
                )}
                <label>
                  Subtopic
                  <input
                    value={createTopicSubtopic}
                    onChange={(e) => setCreateTopicSubtopic(e.target.value)}
                    placeholder="Subtopic (e.g., Macroeconomics)"
                    required
                  />
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  {showAddAnother ? (
                    <>
                      <button type="button" className="btn btn-secondary btn-xs" onClick={handleAddAnother}>
                        Add Another
                      </button>
                      <button type="button" className="btn btn-secondary btn-xs" onClick={() => setShowCreateTopicModal(false)}>
                        Done
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" className="btn btn-secondary btn-xs" onClick={() => setShowCreateTopicModal(false)}>
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary btn-xs" disabled={creatingTopic}>
                        {creatingTopic ? 'Creating…' : 'Create'}
                      </button>
                    </>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Suggestions Modal */}
      {showSuggestionsModal && (
        <div className="admin-modal-overlay" onClick={() => setShowSuggestionsModal(false)}>
          <div className="admin-drawer admin-suggestions-modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '1rem 1rem 0.5rem' }}>
              <h2 style={{ margin: 0 }}>AI Topic Suggestions</h2>
              <p style={{ margin: '0.25rem 0 0', color: 'var(--text-muted, #6b7280)' }}>
                Configure AI suggestions and pick one to create a topic.
              </p>
            </div>
            <div className="admin-suggestions-modal__content">
              <div className="admin-suggestions-modal__config">
                <div className="admin-suggestions-modal__config-inner">
                  <label className="admin-suggestions-modal__label">
                    Filter by main topic (optional)
                    <select
                      value={suggestionMainTopicFilter}
                      onChange={(e) => setSuggestionMainTopicFilter(e.target.value)}
                      className="admin-suggestions-modal__select"
                    >
                      <option value="">All topics</option>
                      {mainTopics.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="admin-suggestions-modal__checkbox-label">
                    <input
                      type="checkbox"
                      checked={suggestionReviewExisting}
                      onChange={(e) => setSuggestionReviewExisting(e.target.checked)}
                      className="admin-suggestions-modal__checkbox"
                    />
                    <span>Review existing topics and suggest gaps</span>
                  </label>
                  <label className="admin-suggestions-modal__label">
                    Custom instructions (optional)
                    <textarea
                      value={suggestionCustomInstructions}
                      onChange={(e) => setSuggestionCustomInstructions(e.target.value)}
                      placeholder="e.g., Focus on science topics with practical applications"
                      rows={6}
                      className="admin-suggestions-modal__textarea"
                    />
                  </label>
                  <button
                    type="button"
                    className="btn btn-primary btn-xs admin-suggestions-modal__button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleLoadSuggestions(e);
                    }}
                    disabled={loadingSuggestions}
                  >
                    {loadingSuggestions ? 'Loading…' : 'Get Suggestions'}
                  </button>
                </div>
              </div>
              <div className="admin-suggestions-modal__results">
                {loadingSuggestions ? (
                  <div className="admin-loading">Loading suggestions…</div>
                ) : suggestionsError ? (
                  <div className="admin-error">
                    <strong>Error:</strong> {suggestionsError}
                  </div>
                ) : suggestions.length === 0 ? (
                  <div className="admin-loading">No suggestions available.</div>
                ) : (
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {suggestions.map((s) => {
                      const key = `${s.topicName}::${s.subtopicName}`;
                      return (
                        <div key={key} className="admin-content-candidate">
                          <div className="admin-content-candidate__q">
                            {s.topicName} · {s.subtopicName}
                          </div>
                          <div className="admin-content-candidate__meta">
                            {s.reasoning} (est. {s.estimatedDifficulty})
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                            <button
                              type="button"
                              className="btn btn-primary btn-xs"
                              disabled={acceptingSuggestion === key}
                              onClick={() => acceptSuggestion(s)}
                            >
                              {acceptingSuggestion === key ? 'Creating…' : 'Create topic'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Question CRUD Modal */}
      {showQuestionModal && (
        <div className="admin-modal-overlay" onClick={() => setShowQuestionModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal__header">
              <h2>{editingQuestion ? 'Edit Question' : 'Create Question'}</h2>
              <button type="button" className="admin-modal__close" onClick={() => setShowQuestionModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSaveQuestion}>
              <div className="admin-modal__body">
                {questionModalError && (
                  <div className="admin-loading">
                    <strong>Error:</strong> {questionModalError}
                  </div>
                )}
                <label>
                  Question text
                  <textarea
                    name="question_text"
                    value={questionForm.question_text}
                    onChange={handleQuestionFormChange}
                    required
                  />
                </label>
                <label>
                  Main topic
                  <select
                    value={questionMainTopicMode === 'existing' ? questionMainTopic : '__new__'}
                    onChange={(e) => {
                      if (e.target.value === '__new__') {
                        setQuestionMainTopicMode('new');
                        setQuestionMainTopic('');
                        // Clear subtopic when main topic changes
                        setQuestionCategoryMode('new');
                        handleQuestionFormChange({ target: { name: 'category', value: '' } } as any);
                      } else {
                        setQuestionMainTopicMode('existing');
                        setQuestionMainTopic(e.target.value);
                        // Reset subtopic when main topic changes
                        setQuestionCategoryMode('existing');
                        handleQuestionFormChange({ target: { name: 'category', value: '' } } as any);
                      }
                    }}
                    required
                  >
                    <option value="" disabled>Select a main topic…</option>
                    {mainTopics.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                    <option value="__new__">Create new…</option>
                  </select>
                </label>
                {questionMainTopicMode === 'new' && (
                  <label>
                    New main topic name
                    <input
                      value={questionMainTopic}
                      onChange={(e) => setQuestionMainTopic(e.target.value)}
                      placeholder="Enter new main topic name"
                      required
                    />
                  </label>
                )}
                <label>
                  Subtopic / Category
                  <select
                    value={questionCategoryMode === 'existing' ? questionForm.category : '__new__'}
                    onChange={(e) => {
                      if (e.target.value === '__new__') {
                        setQuestionCategoryMode('new');
                        handleQuestionFormChange({ target: { name: 'category', value: '' } } as any);
                      } else {
                        setQuestionCategoryMode('existing');
                        handleQuestionFormChange({ target: { name: 'category', value: e.target.value } } as any);
                      }
                    }}
                    required
                    disabled={!questionMainTopic || questionMainTopicMode === 'new'}
                  >
                    <option value="" disabled>Select a subtopic…</option>
                    {questionMainTopic && questionMainTopicMode === 'existing' && topics
                      .find(t => t.mainTopic === questionMainTopic)
                      ?.subtopics.map((s) => (
                        <option key={s.id} value={s.subtopic}>
                          {s.subtopic}
                        </option>
                      ))}
                    <option value="__new__">Create new…</option>
                  </select>
                </label>
                {questionCategoryMode === 'new' && (
                  <label>
                    New subtopic / category name
                    <input
                      name="category"
                      value={questionForm.category}
                      onChange={handleQuestionFormChange}
                      placeholder="Enter new subtopic name"
                      required
                    />
                  </label>
                )}
                <label>
                  Difficulty
                  <select name="difficulty" value={questionForm.difficulty} onChange={handleQuestionFormChange}>
                    <option value="hard">Hard</option>
                    <option value="medium">Medium</option>
                    <option value="easy">Easy</option>
                  </select>
                </label>
                <div className="admin-modal__grid admin-modal__grid--two">
                  <label>
                    Option A
                    <input name="option_a" value={questionForm.option_a} onChange={handleQuestionFormChange} required />
                  </label>
                  <label>
                    Option B
                    <input name="option_b" value={questionForm.option_b} onChange={handleQuestionFormChange} required />
                  </label>
                  <label>
                    Option C
                    <input name="option_c" value={questionForm.option_c} onChange={handleQuestionFormChange} required />
                  </label>
                  <label>
                    Option D
                    <input name="option_d" value={questionForm.option_d} onChange={handleQuestionFormChange} required />
                  </label>
                </div>
                <label>
                  Correct answer
                  <select name="correct_answer" value={questionForm.correct_answer} onChange={handleQuestionFormChange}>
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                    <option value="D">D</option>
                  </select>
                </label>
              </div>
              <div className="admin-modal__footer">
                <button type="button" className="btn btn-secondary btn-xs" onClick={() => setShowQuestionModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-xs" disabled={questionSaving}>
                  {questionSaving ? 'Saving…' : editingQuestion ? 'Save changes' : 'Create question'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Move Subtopic Modal */}
      {showMoveSubtopicModal && (
        <div className="admin-modal-overlay" onClick={() => setShowMoveSubtopicModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal__header">
              <h2>Move subtopic (bulk)</h2>
              <button type="button" className="admin-modal__close" onClick={() => setShowMoveSubtopicModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleMoveSubtopic}>
              <div className="admin-modal__body">
                {moveSubtopicError && (
                  <div className="admin-loading">
                    <strong>Error:</strong> {moveSubtopicError}
                  </div>
                )}
                <div className="admin-content-subtopic__hint">
                  This renames/moves <strong>all questions</strong> currently in one subtopic to another subtopic name.
                </div>
                <div className="admin-modal__grid admin-modal__grid--two">
                  <label>
                    Current subtopic
                    <input value={moveFromCategory} disabled />
                  </label>
                  <label>
                    Current main topic
                    <input value={moveFromMainTopic} disabled />
                  </label>
                </div>

                <div className="admin-modal__grid admin-modal__grid--two">
                  <label>
                    Destination main topic
                    <select
                      value={moveMainTopicMode === 'existing' ? moveSelectedMainTopic : '__new__'}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '__new__') {
                          setMoveMainTopicMode('new');
                          setMoveSelectedMainTopic('');
                          setMoveNewMainTopic('');
                          // reset subtopic selection when switching modes
                          setMoveSubtopicMode('existing');
                          setMoveSelectedSubtopic('');
                          setMoveNewSubtopic('');
                        } else {
                          setMoveMainTopicMode('existing');
                          setMoveSelectedMainTopic(v);
                          // reset subtopic selection when main topic changes
                          setMoveSubtopicMode('existing');
                          setMoveSelectedSubtopic('');
                          setMoveNewSubtopic('');
                        }
                      }}
                      required
                    >
                      <option value="" disabled>
                        Select a main topic…
                      </option>
                      {topics.map((t) => (
                        <option key={t.mainTopic} value={t.mainTopic}>
                          {t.mainTopic}
                        </option>
                      ))}
                      <option value="__new__">Create new…</option>
                    </select>
                  </label>
                  <label>
                    {moveMainTopicMode === 'new' ? 'New main topic name' : ' '}
                    {moveMainTopicMode === 'new' ? (
                      <input
                        value={moveNewMainTopic}
                        onChange={(e) => setMoveNewMainTopic(e.target.value)}
                        placeholder="Enter new main topic…"
                        required
                      />
                    ) : (
                      <div />
                    )}
                  </label>
                </div>

                {(() => {
                  const effectiveMainTopic =
                    moveMainTopicMode === 'existing' ? moveSelectedMainTopic : '';
                  const subtopicOptions =
                    effectiveMainTopic
                      ? Array.from(
                          new Set(
                            (topics.find((t) => t.mainTopic === effectiveMainTopic)?.subtopics || []).map((s) => s.subtopic)
                          )
                        ).sort((a, b) => a.localeCompare(b))
                      : [];

                  return (
                    <div className="admin-modal__grid admin-modal__grid--two">
                      <label>
                        Destination subtopic
                        <select
                          value={moveSubtopicMode === 'existing' ? moveSelectedSubtopic : '__new__'}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === '__new__') {
                              setMoveSubtopicMode('new');
                              setMoveSelectedSubtopic('');
                              setMoveNewSubtopic('');
                            } else {
                              setMoveSubtopicMode('existing');
                              setMoveSelectedSubtopic(v);
                              setMoveNewSubtopic('');
                            }
                          }}
                          disabled={moveMainTopicMode !== 'existing' || !effectiveMainTopic}
                          required
                        >
                          <option value="" disabled>
                            {moveMainTopicMode !== 'existing' || !effectiveMainTopic
                              ? 'Select a main topic first…'
                              : 'Select a subtopic…'}
                          </option>
                          {subtopicOptions.map((st) => (
                            <option key={st} value={st}>
                              {st}
                            </option>
                          ))}
                          <option value="__new__">Create new…</option>
                        </select>
                      </label>
                      <label>
                        {moveSubtopicMode === 'new' ? 'New subtopic name' : ' '}
                        {moveSubtopicMode === 'new' ? (
                          <input
                            value={moveNewSubtopic}
                            onChange={(e) => setMoveNewSubtopic(e.target.value)}
                            placeholder="Enter new subtopic…"
                            required
                          />
                        ) : (
                          <div />
                        )}
                      </label>
                    </div>
                  );
                })()}
              </div>
              <div className="admin-modal__footer">
                <button type="button" className="btn btn-secondary btn-xs" onClick={() => setShowMoveSubtopicModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-xs" disabled={movingSubtopic}>
                  {movingSubtopic ? 'Moving…' : 'Move all questions'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Duplicate Cleanup Modal */}
      {showDuplicateModal && (
        <div className="admin-modal-overlay" onClick={() => setShowDuplicateModal(false)}>
          <div className="admin-drawer" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', width: '90%' }}>
            <div style={{ padding: '1rem 1rem 0.5rem' }}>
              <h2 style={{ margin: 0 }}>Clean Up Duplicate Questions</h2>
              <p style={{ margin: '0.25rem 0 0', color: 'var(--text-muted, #6b7280)' }}>
                Review detected duplicate questions. The oldest question in each group will be kept.
              </p>
            </div>
            <div style={{ padding: '1rem', overflowY: 'auto', maxHeight: '70vh' }}>
              {loadingDuplicates ? (
                <div className="admin-loading">Detecting duplicates…</div>
              ) : duplicatesError ? (
                <div className="admin-error">
                  <strong>Error:</strong> {duplicatesError}
                </div>
              ) : duplicateGroups.length === 0 ? (
                <p className="admin-loading">No duplicate questions found.</p>
              ) : (
                <>
                  <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'var(--color-surface-muted, #f3f4f6)', borderRadius: '4px' }}>
                    <strong>Found {duplicateGroups.length} duplicate group(s) with {totalDuplicates} duplicate question(s) total.</strong>
                  </div>
                  <div style={{ display: 'grid', gap: '1rem', maxHeight: '50vh', overflowY: 'auto' }}>
                    {duplicateGroups.map((group, groupIndex) => {
                      const [keepQuestion, ...duplicateQuestions] = group.duplicates;
                      return (
                        <div key={groupIndex} style={{ border: '1px solid var(--color-border, #e5e7eb)', borderRadius: '4px', padding: '1rem' }}>
                          <div style={{ marginBottom: '0.75rem', fontWeight: 600, color: 'var(--color-text, #111827)' }}>
                            Group {groupIndex + 1} ({group.duplicates.length} questions)
                          </div>
                          <div style={{ display: 'grid', gap: '0.75rem' }}>
                            <div style={{ padding: '0.75rem', background: 'var(--color-success-light, #d1fae5)', borderRadius: '4px', border: '1px solid var(--color-success, #10b981)' }}>
                              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-success, #10b981)', marginBottom: '0.25rem' }}>
                                KEEPING (oldest)
                              </div>
                              <div style={{ fontSize: '14px', marginBottom: '0.25rem' }}>{keepQuestion.question_text}</div>
                              <div style={{ fontSize: '12px', color: 'var(--color-text-muted, #6b7280)' }}>
                                ID: {keepQuestion.id} • {keepQuestion.category} • {keepQuestion.difficulty} • Created: {new Date(keepQuestion.created_at).toLocaleDateString()}
                              </div>
                            </div>
                            {duplicateQuestions.map((dup) => (
                              <div key={dup.id} style={{ padding: '0.75rem', background: 'var(--color-surface-muted, #f3f4f6)', borderRadius: '4px', border: '1px solid var(--color-border, #e5e7eb)' }}>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-error, #ef4444)', marginBottom: '0.25rem' }}>
                                  DELETING
                                </div>
                                <div style={{ fontSize: '14px', marginBottom: '0.25rem' }}>{dup.question_text}</div>
                                <div style={{ fontSize: '12px', color: 'var(--color-text-muted, #6b7280)' }}>
                                  ID: {dup.id} • {dup.category} • {dup.difficulty} • Created: {new Date(dup.created_at).toLocaleDateString()}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            {!loadingDuplicates && duplicateGroups.length > 0 && (
              <div style={{ padding: '1rem', borderTop: '1px solid var(--color-border, #e5e7eb)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: '14px', color: 'var(--color-text-muted, #6b7280)' }}>
                  {totalDuplicates} duplicate question(s) will be deleted
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    type="button"
                    className="btn btn-secondary btn-xs"
                    onClick={() => setShowDuplicateModal(false)}
                    disabled={cleaningDuplicates}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-xs"
                    onClick={handleCleanupDuplicates}
                    disabled={cleaningDuplicates}
                  >
                    {cleaningDuplicates ? 'Cleaning up…' : 'Delete All Duplicates'}
                  </button>
                </div>
              </div>
            )}
            {!loadingDuplicates && duplicateGroups.length === 0 && !duplicatesError && (
              <div style={{ padding: '1rem', borderTop: '1px solid var(--color-border, #e5e7eb)', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary btn-xs"
                  onClick={() => setShowDuplicateModal(false)}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && confirmDialogConfig && (
        <div className="admin-modal-overlay" onClick={handleCancel}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="admin-modal__header">
              <h2>Confirm Action</h2>
              <button type="button" className="admin-modal__close" onClick={handleCancel}>
                ×
              </button>
            </div>
            <div className="admin-modal__body">
              <p>{confirmDialogConfig.message}</p>
            </div>
            <div className="admin-modal__footer">
              <button type="button" className="btn btn-secondary btn-xs" onClick={handleCancel}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary btn-xs" onClick={handleConfirm}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Topic Modal */}
      {showEditTopicModal && (
        <div className="admin-modal-overlay" onClick={() => setShowEditTopicModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal__header">
              <h2>Edit Topic</h2>
              <button type="button" className="admin-modal__close" onClick={() => setShowEditTopicModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSaveTopicEdit}>
              <div className="admin-modal__body">
                {editError && (
                  <div className="admin-loading">
                    <strong>Error:</strong> {editError}
                  </div>
                )}
                <label>
                  Topic name
                  <input
                    value={editingTopicName}
                    onChange={(e) => setEditingTopicName(e.target.value)}
                    required
                  />
                </label>
              </div>
              <div className="admin-modal__footer">
                <button type="button" className="btn btn-secondary btn-xs" onClick={() => setShowEditTopicModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-xs" disabled={savingEdit}>
                  {savingEdit ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Subtopic Modal */}
      {showEditSubtopicModal && (
        <div className="admin-modal-overlay" onClick={() => setShowEditSubtopicModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal__header">
              <h2>Edit Subtopic</h2>
              <button type="button" className="admin-modal__close" onClick={() => setShowEditSubtopicModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSaveSubtopicEdit}>
              <div className="admin-modal__body">
                {editError && (
                  <div className="admin-loading">
                    <strong>Error:</strong> {editError}
                  </div>
                )}
                <label>
                  Main topic
                  <select
                    value={editingSubtopicMainTopicMode === 'existing' ? editingSubtopicMainTopic : '__new__'}
                    onChange={(e) => {
                      if (e.target.value === '__new__') {
                        setEditingSubtopicMainTopicMode('new');
                        setEditingSubtopicMainTopic('');
                      } else {
                        setEditingSubtopicMainTopicMode('existing');
                        setEditingSubtopicMainTopic(e.target.value);
                      }
                    }}
                    required
                  >
                    <option value="" disabled>Select a main topic…</option>
                    {mainTopics.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                    <option value="__new__">Create new…</option>
                  </select>
                </label>
                {editingSubtopicMainTopicMode === 'new' && (
                  <label>
                    New main topic name
                    <input
                      value={editingSubtopicMainTopic}
                      onChange={(e) => setEditingSubtopicMainTopic(e.target.value)}
                      placeholder="Enter new main topic name"
                      required
                    />
                  </label>
                )}
                <label>
                  Subtopic name
                  <select
                    value={editingSubtopicNameMode === 'existing' ? editingSubtopicName : '__new__'}
                    onChange={(e) => {
                      if (e.target.value === '__new__') {
                        setEditingSubtopicNameMode('new');
                        setEditingSubtopicName('');
                      } else {
                        setEditingSubtopicNameMode('existing');
                        setEditingSubtopicName(e.target.value);
                      }
                    }}
                    required
                  >
                    <option value="" disabled>Select a subtopic…</option>
                    {editingSubtopicMainTopic && topics
                      .find(t => t.mainTopic === editingSubtopicMainTopic)
                      ?.subtopics.map((s) => (
                        <option key={s.id} value={s.subtopic}>
                          {s.subtopic}
                        </option>
                      ))}
                    <option value="__new__">Create new…</option>
                  </select>
                </label>
                {editingSubtopicNameMode === 'new' && (
                  <label>
                    New subtopic name
                    <input
                      value={editingSubtopicName}
                      onChange={(e) => setEditingSubtopicName(e.target.value)}
                      placeholder="Enter new subtopic name"
                      required
                    />
                  </label>
                )}
              </div>
              <div className="admin-modal__footer">
                <button type="button" className="btn btn-secondary btn-xs" onClick={() => setShowEditSubtopicModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-xs" disabled={savingEdit}>
                  {savingEdit ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContentManager;
